import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import type { Employer, EmployerOption, NamedEntity, ApiFeature, TagCategory } from '../types/bulkUpload';

interface UseEmployerDataReturn {
  employers: Employer[];
  loadingEmployers: boolean;
  selectedEmployer: EmployerOption | null;
  setSelectedEmployer: (option: EmployerOption | null) => void;
  availableRoles: NamedEntity[];
  availableFeatures: NamedEntity[];
  availableBranches: NamedEntity[];
  availableTags: NamedEntity[];
  loadingEmployerData: boolean;
}

const useEmployerData = (): UseEmployerDataReturn => {
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [loadingEmployers, setLoadingEmployers] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState<EmployerOption | null>(null);
  const [availableRoles, setAvailableRoles] = useState<NamedEntity[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<NamedEntity[]>([]);
  const [availableTags, setAvailableTags] = useState<NamedEntity[]>([]);
  const [loadingEmployerData, setLoadingEmployerData] = useState(false);

  const availableBranches = useMemo(() => {
    if (!selectedEmployer) return [];
    const employer = employers.find((e) => e.id === selectedEmployer.value);
    return employer?.branches || [];
  }, [selectedEmployer, employers]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    Promise.all([
      axios.get<Employer[]>(`${environment.baseUrl}/employers/`, { signal }),
      axios.get<NamedEntity[]>(`${environment.baseUrl}/user_roles?roles_for=all`, { signal }),
    ]).then(([employersRes, rolesRes]) => {
      setEmployers(employersRes.data.filter((emp) => emp.status !== 'deleted'));
      setAvailableRoles(rolesRes.data || []);
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        apiResponseHandler.handleApiError('Error loading employers', error);
      }
    }).finally(() => {
      if (!signal.aborted) setLoadingEmployers(false);
    });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedEmployer) {
      // Branches are memo-derived from employers, so no explicit reset needed
      setAvailableFeatures([]);
      setAvailableTags([]);
      setLoadingEmployerData(false);
      return undefined;
    }

    const controller = new AbortController();
    const { signal } = controller;

    setAvailableFeatures([]);
    setAvailableTags([]);
    setLoadingEmployerData(true);

    Promise.all([
      axios.get<ApiFeature[]>(`${environment.baseUrl}/employer_features`, {
        params: { employer_id: selectedEmployer.value, enabled: true },
        signal,
      }),
      axios.get<TagCategory[]>(`${environment.baseUrl}/employertag/categories/`, {
        params: { employer_id: selectedEmployer.value },
        signal,
      }),
    ])
      .then(([featuresRes, tagsRes]) => {
        setAvailableFeatures(
          (featuresRes.data || []).map((f) => ({
            id: f.id,
            name: f.name || f.feature_name || '',
          })),
        );

        const allTags: NamedEntity[] = (tagsRes.data || []).flatMap(
          (category) => category.tags || []
        );
        setAvailableTags(allTags);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setAvailableFeatures([]);
          setAvailableTags([]);
          apiResponseHandler.handleApiError('Error loading employer data', error);
        }
      })
      .finally(() => {
        if (!signal.aborted) setLoadingEmployerData(false);
      });

    return () => controller.abort();
  }, [selectedEmployer]);

  return {
    employers,
    loadingEmployers,
    selectedEmployer,
    setSelectedEmployer,
    availableRoles,
    availableFeatures,
    availableBranches,
    availableTags,
    loadingEmployerData,
  };
};

export default useEmployerData;
